import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import NavSocialMedia from "../components/NavSocialMedia.jsx";
import API from "../config/api.js";
import { useAuth } from "../context/AuthContext.jsx";

const DEFAULT_DAYS = {
  0: 0,
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
};

const DAY_LABELS = [
  { key: 0, label: "Su" },
  { key: 1, label: "Mo" },
  { key: 2, label: "Tu" },
  { key: 3, label: "We" },
  { key: 4, label: "Th" },
  { key: 5, label: "Fr" },
  { key: 6, label: "Sa" },
];

function parseBrands(brands) {
  if (!brands) return [];
  if (Array.isArray(brands)) return brands.map(String);

  return String(brands)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseDaystopost(daystopost) {
  if (typeof daystopost === "object" && daystopost !== null) {
    return { ...DEFAULT_DAYS, ...daystopost };
  }

  try {
    const parsed = JSON.parse(daystopost || "{}");
    return { ...DEFAULT_DAYS, ...parsed };
  } catch {
    return { ...DEFAULT_DAYS };
  }
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function getTimezoneOptions() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone").map((tz) => {
        const now = new Date();
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: tz,
          timeZoneName: "shortOffset",
        }).formatToParts(now);

        const tzName =
          parts.find((part) => part.type === "timeZoneName")?.value || "GMT";

        return {
          value: tz,
          label: `(${tzName}) ${tz}`,
        };
      });
    }
  } catch (err) {
    console.error("Erro ao gerar timezones:", err);
  }

  const fallback = getBrowserTimezone();

  return [
    { value: fallback, label: fallback },
    { value: "UTC", label: "UTC" },
  ];
}

function buildFormFromAuth(user, userCategories = []) {
  const nicheCategories = userCategories
    .filter((item) => item.type === "doneforyou")
    .map((item) => String(item.idcategory));

  const destinationCategories = userCategories
    .filter((item) => item.type === "destination")
    .map((item) => String(item.idcategory));

  return {
    personalizedcontent:
      Number(user?.personalizedcontent) === 1 ||
      user?.personalizedcontent === true,
    website: user?.website || "",
    name: user?.name || "",
    businessname: user?.businessname || "",
    teamsize: Number(user?.teamsize) > 0 ? Number(user.teamsize) : 1,
    nicheCategories,
    destinationCategories,
    brands: parseBrands(user?.brands),
    clientdescription: user?.clientdescription || "",
    timezonelocation: user?.timezonelocation || getBrowserTimezone(),
    daystopost: parseDaystopost(user?.daystopost),
    timetopost: user?.timetopost || "13:20",
  };
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userCategories, loading } = useAuth();

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const nicheSelectRef = useRef(null);
  const destinationSelectRef = useRef(null);
  const brandsInputRef = useRef(null);

  const nicheChoicesRef = useRef(null);
  const destinationChoicesRef = useRef(null);
  const brandsChoicesRef = useRef(null);

  const syncingNicheRef = useRef(false);
  const syncingDestinationRef = useRef(false);
  const syncingBrandsRef = useRef(false);

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(() =>
    buildFormFromAuth(user, userCategories),
  );

  const [currentStep, setCurrentStep] = useState(
    Number(searchParams.get("step")) || 1,
  );

  const placeholders = Array.from({ length: 6 });

  const nicheOptions = useMemo(
    () => categories.filter((item) => item.type === "doneforyou"),
    [categories],
  );

  const destinationOptions = useMemo(
    () => categories.filter((item) => item.type === "destination"),
    [categories],
  );

  const saveOnboarding = useCallback(async (override = null) => {
    const payload = override || form;

    try {
      const body = new URLSearchParams();

      body.append(
        "personalizedcontent",
        payload.personalizedcontent ? "1" : "0",
      );
      body.append("website", payload.website || "");
      body.append("name", payload.name || "");
      body.append("businessname", payload.businessname || "");
      body.append("teamsize", String(payload.teamsize || 1));
      body.append("clientdescription", payload.clientdescription || "");
      body.append("timezonelocation", payload.timezonelocation || "");
      body.append(
        "brands",
        Array.isArray(payload.brands) ? payload.brands.join(",") : "",
      );
      body.append(
        "daystopost",
        JSON.stringify(payload.daystopost || DEFAULT_DAYS),
      );
      body.append("timetopost", payload.timetopost || "13:20");

      [...payload.nicheCategories, ...payload.destinationCategories].forEach(
        (idcategory) => body.append("idcategory[]", idcategory),
      );

      await fetch(`${API.account}?savePreferences`, {
        method: "POST",
        body,
        credentials: "include",
      });

      if (typeof window._alert === "function") {
        window._alert("success", "Saved");
      }
    } catch (error) {
      console.error("Error saving onboarding:", error);
    }
  }, [form]);

  const updateField = useCallback((field, value, autoSave = true) => {
    setForm((prev) => {
      const nextForm = {
        ...prev,
        [field]: value,
      };

      if (autoSave) {
        saveOnboarding(nextForm);
      }

      return nextForm;
    });
  }, [saveOnboarding]);

  const handleNicheChange = useCallback(() => {
    if (syncingNicheRef.current) return;

    const values = nicheChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item))
      : values
        ? [String(values)]
        : [];

    updateField("nicheCategories", normalized);
  }, [updateField]);

  const handleDestinationChange = useCallback(() => {
    if (syncingDestinationRef.current) return;

    const values = destinationChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item))
      : values
        ? [String(values)]
        : [];

    updateField("destinationCategories", normalized);
  }, [updateField]);

  const syncBrandsFromChoices = useCallback(() => {
    if (syncingBrandsRef.current) return;

    const values = brandsChoicesRef.current?.getValue(true);
    const normalized = Array.isArray(values)
      ? values.map((item) => String(item).trim()).filter(Boolean)
      : values
        ? [String(values).trim()]
        : [];

    setForm((prev) => {
      const same =
        prev.brands.length === normalized.length &&
        prev.brands.every((item, index) => item === normalized[index]);

      if (same) return prev;

      const nextForm = {
        ...prev,
        brands: normalized,
      };

      saveOnboarding(nextForm);
      return nextForm;
    });
  }, [saveOnboarding]);

  const toggleDay = useCallback((day) => {
    setForm((prev) => {
      const nextDays = {
        ...prev.daystopost,
        [day]: prev.daystopost[day] === 1 ? 0 : 1,
      };

      const nextForm = {
        ...prev,
        daystopost: nextDays,
      };

      saveOnboarding(nextForm);
      return nextForm;
    });
  }, [saveOnboarding]);

  const step = (index) => {
    setCurrentStep(index);
  };

  const closeAndGo = (path) => {
    if (window.bootstrap) {
      const modalEl = document.getElementById(`step${currentStep}Modal`);
      if (modalEl) {
        const instance = window.bootstrap.Modal.getInstance(modalEl);
        instance?.hide();
      }
    }

    navigate(path);
  };

  const renderStepDots = (activeStep) => (
    <div className="align-items-center d-flex">
      {Array.from({ length: 7 }).map((_, index) => {
        const stepNumber = index + 1;

        if (stepNumber === activeStep) {
          return (
            <span
              key={stepNumber}
              className="badge bg-secondary-light text-primary px-3 py-2 rounded-4 mx-1"
            >
              STEP {stepNumber}
            </span>
          );
        }

        return (
          <span
            key={stepNumber}
            className="bg-secondary-light rounded-circle border d-inline-block mx-1"
            style={{ width: "8px", height: "8px" }}
          ></span>
        );
      })}
    </div>
  );

  useEffect(() => {
    if (!loading) {
      setForm(buildFormFromAuth(user, userCategories));
    }
  }, [user, userCategories, loading]);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch(`${API.categories}?list&json=true`, {
          credentials: "include",
        });
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error loading categories:", error);
        setCategories([]);
      }
    }

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!window.bootstrap) return;

    const draftPromptEl = document.getElementById("draft-prompt");
    if (draftPromptEl) {
      const draftPromptInstance =
        window.bootstrap.Modal.getOrCreateInstance(draftPromptEl);
      draftPromptInstance.hide();
    }
  }, []);

  useEffect(() => {
    if (!window.bootstrap) return;

    document.querySelectorAll(".modal").forEach((modalEl) => {
      const instance = window.bootstrap.Modal.getOrCreateInstance(modalEl);
      instance.hide();
    });

    const activeModalEl = document.getElementById(`step${currentStep}Modal`);
    if (activeModalEl) {
      const activeInstance =
        window.bootstrap.Modal.getOrCreateInstance(activeModalEl);
      activeInstance.show();
    }
  }, [currentStep]);

  useEffect(() => {
    if (!window.Choices) return;

    const nicheElement = nicheSelectRef.current;
    const destinationElement = destinationSelectRef.current;

    if (!nicheElement || !destinationElement) return;

    if (!nicheChoicesRef.current) {
      nicheChoicesRef.current = new window.Choices(nicheElement, {
        searchEnabled: true,
        searchChoices: true,
        searchPlaceholderValue: "Search",
        placeholder: true,
        removeItemButton: true,
        maxItemCount: 3,
        shouldSort: false,
        itemSelectText: "",
      });

      nicheElement.addEventListener("change", handleNicheChange);
    }

    if (!destinationChoicesRef.current) {
      destinationChoicesRef.current = new window.Choices(destinationElement, {
        searchEnabled: true,
        searchChoices: true,
        searchPlaceholderValue: "Search",
        placeholder: true,
        removeItemButton: true,
        maxItemCount: 3,
        shouldSort: false,
        itemSelectText: "",
      });

      destinationElement.addEventListener("change", handleDestinationChange);
    }

    return () => {
      nicheElement.removeEventListener("change", handleNicheChange);
      destinationElement.removeEventListener("change", handleDestinationChange);

      nicheChoicesRef.current?.destroy();
      destinationChoicesRef.current?.destroy();

      nicheChoicesRef.current = null;
      destinationChoicesRef.current = null;
    };
  }, [handleNicheChange, handleDestinationChange]);

  useEffect(() => {
    if (!window.Choices) return;
    if (loading) return;

    const brandsElement = brandsInputRef.current;
    if (!brandsElement) return;

    if (brandsChoicesRef.current) {
      brandsElement.removeEventListener("addItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("removeItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("change", syncBrandsFromChoices);

      brandsChoicesRef.current.destroy();
      brandsChoicesRef.current = null;
    }

    syncingBrandsRef.current = true;

    brandsElement.value = Array.isArray(form.brands)
      ? form.brands.join(",")
      : "";

    brandsChoicesRef.current = new window.Choices(brandsElement, {
      removeItemButton: true,
      duplicateItemsAllowed: false,
      maxItemCount: 5,
      shouldSort: false,
      itemSelectText: "",
      addItems: true,
      editItems: true,
      delimiter: ",",
      paste: true,
      placeholder: true,
      placeholderValue: "Choose your option",
      addItemFilter: (value) => String(value).trim().length > 0,
    });

    brandsElement.addEventListener("addItem", syncBrandsFromChoices);
    brandsElement.addEventListener("removeItem", syncBrandsFromChoices);
    brandsElement.addEventListener("change", syncBrandsFromChoices);

    requestAnimationFrame(() => {
      syncingBrandsRef.current = false;
    });

    return () => {
      brandsElement.removeEventListener("addItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("removeItem", syncBrandsFromChoices);
      brandsElement.removeEventListener("change", syncBrandsFromChoices);

      brandsChoicesRef.current?.destroy();
      brandsChoicesRef.current = null;
    };
  }, [loading, form.brands, syncBrandsFromChoices]);

  useEffect(() => {
    if (!nicheChoicesRef.current) return;
    if (!nicheOptions.length) return;

    syncingNicheRef.current = true;

    nicheChoicesRef.current.clearChoices();
    nicheChoicesRef.current.setChoices(
      nicheOptions.map((item) => ({
        value: String(item.idcategory),
        label: item.title,
        selected: false,
      })),
      "value",
      "label",
      true,
    );

    nicheChoicesRef.current.removeActiveItems();

    form.nicheCategories.forEach((value) => {
      nicheChoicesRef.current.setChoiceByValue(String(value));
    });

    requestAnimationFrame(() => {
      syncingNicheRef.current = false;
    });
  }, [nicheOptions, form.nicheCategories]);

  useEffect(() => {
    if (!destinationChoicesRef.current) return;
    if (!destinationOptions.length) return;

    syncingDestinationRef.current = true;

    destinationChoicesRef.current.clearChoices();
    destinationChoicesRef.current.setChoices(
      destinationOptions.map((item) => ({
        value: String(item.idcategory),
        label: item.title,
        selected: false,
      })),
      "value",
      "label",
      true,
    );

    destinationChoicesRef.current.removeActiveItems();

    form.destinationCategories.forEach((value) => {
      destinationChoicesRef.current.setChoiceByValue(String(value));
    });

    requestAnimationFrame(() => {
      syncingDestinationRef.current = false;
    });
  }, [destinationOptions, form.destinationCategories]);

  return (
    <AppLayout currentPage="social-media">
      <NavSocialMedia currentPage="social-media" />
      <style>{`
        .close-onboarding {
          padding: 0 !important;
        }
      `}</style>

      <div className="row g-2" id="caption-list">
        {placeholders.map((_, index) => (
          <div className="col-md-4 placeholder-item" key={index}>
            <div className="card rounded-4 h-100 border caption animate__animated animate__fadeIn animate__slow">
              <div className="card-header bg-white rounded-4 border-0 p-3 pb-0">
                <div className="mb-2 categories">
                  <span className="placeholder col-6"></span>
                </div>
                <h5 className="card-title fs-5 mb-0">
                  <span className="placeholder col-8"></span>
                </h5>
              </div>

              <div className="card-body">
                <p className="card-text caption-content">
                  <span className="placeholder col-7"></span>
                  <br />
                  <span className="placeholder col-4"></span>
                  <br />
                  <span className="placeholder col-6"></span>
                  <br />
                  <span className="placeholder col-8"></span>
                  <br />
                </p>
              </div>

              <div className="card-footer rounded-bottom-4 bg-transparent p-3">
                <div className="row align-items-center">
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-outline-primary disabled placeholder"
                      disabled
                    >
                      + Use
                    </button>
                  </div>
                  <div className="col text-end action-buttons">
                    <span className="placeholder col-1 me-2"></span>
                    <span className="placeholder col-1 me-2"></span>
                    <span className="placeholder col-1"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <form id="onboarding">
        <div
          className="modal"
          id="step1Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(1)}
                      </div>

                      <div className="my-auto">
                        <h3>
                          Welcome! Let&apos;s get you marketing like a pro. The
                          right content starts with the right setup.
                        </h3>
                        <p>
                          Help us understand your business so we can personalize
                          your experience. Answer a few quick questions, and
                          we’ll tailor your content suggestions, calendar, and
                          captions to fit your niche and audience.
                        </p>

                        <h6 className="mt-5 fw-bold">
                          Consent to Personalize Content
                        </h6>
                        <p className="text-muted">
                          To make your content truly yours, we&apos;ll scan your
                          public website and social profiles for inspiration —
                          focusing only on your voice, story, and style.
                          We&apos;ll never use your data for ads, spam, or
                          anything outside of crafting your personalized
                          content.
                        </p>

                        <div className="form-check">
                          <input
                            className="form-check-input rounded-circle fs-6"
                            type="checkbox"
                            id="personalizedcontent"
                            checked={form.personalizedcontent}
                            onChange={(e) =>
                              updateField(
                                "personalizedcontent",
                                e.target.checked,
                              )
                            }
                          />
                          <label
                            className="form-check-label fw-bold"
                            htmlFor="personalizedcontent"
                          >
                            I&apos;m happy to let you personalize my content
                            this way!
                          </label>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(2)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding1.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step2Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(2)}
                      </div>

                      <div className="my-auto">
                        <h3>Welcome! Set Up Your Profile</h3>
                        <p className="mb-5">
                          Tell us a bit about yourself and your business so we
                          can offer a personalized experience and solutions.
                        </p>

                        <div className="row g-4">
                          <div className="col-md-6">
                            <label className="fw-bold small mb-1">
                              Your Website URL
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              placeholder="Paste the link to your main website"
                              value={form.website}
                              onChange={(e) =>
                                updateField("website", e.target.value, false)
                              }
                              onBlur={() =>
                                updateField("website", form.website, true)
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="fw-bold small mb-1">Name</label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              placeholder="Name as you'd like it to appear"
                              value={form.name}
                              onChange={(e) =>
                                updateField("name", e.target.value, false)
                              }
                              onBlur={() =>
                                updateField("name", form.name, true)
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="fw-bold small mb-1">
                              Business Name
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              placeholder="Name that is associated with your brand"
                              value={form.businessname}
                              onChange={(e) =>
                                updateField(
                                  "businessname",
                                  e.target.value,
                                  false,
                                )
                              }
                              onBlur={() =>
                                updateField(
                                  "businessname",
                                  form.businessname,
                                  true,
                                )
                              }
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="fw-bold small mb-1">
                              Team Size
                            </label>
                            <input
                              type="number"
                              min="1"
                              className="form-control form-control-lg"
                              placeholder="Choose your option"
                              value={form.teamsize}
                              onChange={(e) =>
                                updateField(
                                  "teamsize",
                                  Number(e.target.value) > 0
                                    ? Number(e.target.value)
                                    : 1,
                                  false,
                                )
                              }
                              onBlur={() =>
                                updateField("teamsize", form.teamsize, true)
                              }
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(1)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(3)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding2.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step3Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(3)}
                      </div>

                      <div className="my-auto">
                        <h3>What kind of travel do you specialize in?</h3>
                        <p className="mb-4">
                          Think about who you serve and the types of travel you
                          book.
                        </p>

                        <div className="row g-4">
                          <div className="col-md-12">
                            <label className="fw-bold small mb-1">Niche</label>
                            <select
                              ref={nicheSelectRef}
                              className="form-select mb-5"
                              multiple
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(2)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(4)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding3.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step4Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(4)}
                      </div>

                      <div className="my-auto">
                        <h3>
                          What destinations do you specialize in booking for
                          clients?
                        </h3>
                        <p className="mb-4">
                          These are the places you book most or want to book
                          more of.
                        </p>

                        <div className="row g-4">
                          <div className="col-md-12">
                            <label className="fw-bold small mb-1">
                              Top Destinations
                            </label>
                            <select
                              ref={destinationSelectRef}
                              className="form-select"
                              multiple
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(3)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(5)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding4.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step5Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(5)}
                      </div>

                      <div className="my-auto">
                        <h3>What are the top travel brands you book?</h3>
                        <p className="mb-4">
                          Share hotel groups, cruise lines and tour operators
                          you actively book.
                        </p>

                        <div className="row g-4">
                          <div className="col-md-12">
                            <label className="fw-bold small mb-1">
                              Favorite Brands
                            </label>
                            <input
                              ref={brandsInputRef}
                              type="text"
                              name="brands"
                              className="form-select text-input-choices"
                              defaultValue=""
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(4)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(6)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding5.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step6Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(6)}
                      </div>

                      <div className="my-auto">
                        <h3>
                          Tell us everything you know about your ideal client
                        </h3>
                        <p className="mb-4">
                          Who do you get most excited to plan for? Speak to
                          their lifestyle, priorities, and what lights them up.
                        </p>

                        <div className="row g-4">
                          <div className="col-md-12">
                            <label className="fw-bold small mb-1">
                              Dream Client Avatar
                            </label>
                            <textarea
                              rows="3"
                              className="form-control form-control-lg"
                              placeholder="Professional couples looking for meaningful trips with a touch of luxury. Their priorities are cultural immersion, food, beautiful hotels and easy planning."
                              value={form.clientdescription}
                              onChange={(e) =>
                                updateField(
                                  "clientdescription",
                                  e.target.value,
                                  false,
                                )
                              }
                              onBlur={() =>
                                updateField(
                                  "clientdescription",
                                  form.clientdescription,
                                  true,
                                )
                              }
                            ></textarea>
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(5)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => step(7)}
                          >
                            Next <i className="fi fi-rr-arrow-small-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding6.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step7Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content rounded-4">
              <div className="modal-body">
                <div className="row g-4 align-items-stretch">
                  <div className="col-md-7">
                    <div className="d-flex flex-column h-100 px-5">
                      <div className="d-flex justify-content-between align-items-center py-4">
                        <div>
                          <button
                            type="button"
                            className="btn close-onboarding"
                            style={{ padding: 0 }}
                            onClick={() => closeAndGo("/for-you")}
                          >
                            <i className="fi fi-rr-cross me-2"></i> Cancel
                          </button>
                        </div>
                        {renderStepDots(7)}
                      </div>

                      <div className="my-auto">
                        <h3>
                          Connect your socials & choose your posting schedule
                        </h3>
                        <p className="mb-4">
                          Ready To Set Up Your Social Media Scheduler?
                        </p>

                        <label className="fw-bold mb-2">
                          Connect your social netorks
                        </label>

                        <div className="row g-2">
                          <div className="col-6">
                            <a
                              className="text-decoration-none"
                              href={`${API.account}?connectFacebook`}
                            >
                              <div className="bg-secondary-light p-3 rounded-4">
                                <img
                                  src="assets/img/facebook.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                                <small className="ms-2 fw-bold">Facebook</small>
                                <img
                                  className="float-end"
                                  src="assets/img/travelagentcollective/icon-plus.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                              </div>
                            </a>
                          </div>

                          <div className="col-6">
                            <a
                              className="text-decoration-none"
                              href={`${API.account}?connectInstagram`}
                            >
                              <div className="bg-secondary-light p-3 rounded-4">
                                <img
                                  src="assets/img/instagram.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                                <small className="ms-2 fw-bold">
                                  Instagram
                                </small>
                                <img
                                  className="float-end"
                                  src="assets/img/travelagentcollective/icon-plus.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                              </div>
                            </a>
                          </div>

                          <div className="col-6">
                            <a
                              className="text-decoration-none"
                              href={`${API.account}?connectLinkedin`}
                            >
                              <div className="bg-secondary-light p-3 rounded-4">
                                <img
                                  src="assets/img/linkedin.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                                <small className="ms-2 fw-bold">Linkedin</small>
                                <img
                                  className="float-end"
                                  src="assets/img/travelagentcollective/icon-plus.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                              </div>
                            </a>
                          </div>

                          <div className="col-6">
                            <a
                              className="text-decoration-none"
                              href={`${API.account}?connectTiktok`}
                            >
                              <div className="bg-secondary-light p-3 rounded-4">
                                <img
                                  src="assets/img/tiktok.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                                <small className="ms-2 fw-bold">TikTok</small>
                                <img
                                  className="float-end"
                                  src="assets/img/travelagentcollective/icon-plus.png"
                                  style={{ height: "30px", width: "30px" }}
                                  alt=""
                                />
                              </div>
                            </a>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="fw-bold mb-1">
                            What&apos;s your preferred time zone for scheduling?
                          </label>

                          <select
                            className="form-select form-select-lg mb-4 w-75"
                            value={form.timezonelocation}
                            onChange={(e) =>
                              updateField(
                                "timezonelocation",
                                e.target.value,
                                true,
                              )
                            }
                          >
                            {timezoneOptions.map((timezone) => (
                              <option
                                key={timezone.value}
                                value={timezone.value}
                              >
                                {timezone.label}
                              </option>
                            ))}
                          </select>

                          <label className="fw-bold mb-1">
                            Choose your posting schedule
                          </label>

                          <div
                            className="d-flex flex-wrap gap-2"
                            id="week-buttons"
                          >
                            {DAY_LABELS.map((day) => (
                              <button
                                key={day.key}
                                type="button"
                                className={`btn btn-outline-primary ${
                                  Number(form.daystopost[day.key]) === 1
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => toggleDay(day.key)}
                              >
                                {day.label}
                              </button>
                            ))}

                            <span className="d-inline-block border-end mx-2"></span>

                            <input
                              type="time"
                              className="form-control form-control-lg w-auto"
                              value={form.timetopost}
                              onChange={(e) =>
                                updateField("timetopost", e.target.value, false)
                              }
                              onBlur={() =>
                                updateField("timetopost", form.timetopost, true)
                              }
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            className="btn btn-secondary me-2"
                            onClick={() => step(6)}
                          >
                            <i className="fi fi-rr-arrow-small-left"></i> Back
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={async () => {
                              await saveOnboarding();
                              step(8);
                            }}
                          >
                            Save <i className="fi fi-rr-check"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <img
                      src="assets/img/travelagentcollective/onboarding7.png"
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal"
          id="step8Modal"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-secondary-light modal-md rounded-4">
              <div className="modal-body">
                <div className="p-5 text-center">
                  <img
                    src="assets/img/travelagentcollective/onboarding8.png"
                    className="img-fluid"
                    alt=""
                  />

                  <h3>
                    You&apos;re all set. We’re setting up your personalized
                    profile.
                  </h3>
                  <p>
                    You can update your responses anytime under “My Preferences”
                    in the top right corner.
                  </p>

                  <div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => closeAndGo("/welcome")}
                    >
                      Go to For You
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </AppLayout>
  );
}